import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { combineLatest, empty, EMPTY, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, withLatestFrom, tap, take, skipWhile, skipUntil } from 'rxjs/operators';
import { AppState } from '../../core/core.state';
import * as ToastrActions from '../../core/store/toastr.actions';
import * as LayoutActions from '../../core/store/layout.actions';
import { ConversationsService } from '../../core/services/conversations.service';
import * as VisitorsActions from './visitors.actions';
import * as ActivityActions from './activity.actions';
import { allVisitorsLoaded, selectVisitorById, selectCurrentVisitor, selectSelectedAgent, selectSelectedAgentId, selectVisitorExists } from './visitors.selectors';
import { Update } from '@ngrx/entity';
import { Visitor } from '../../models/visitor.model';
import { Router } from '@angular/router';
import { currentUser } from '../../core/auth';
import { selectViewMode, selectSelectedProperty, selectVisitorPerCurrentView, selectInboxView, selectCustomViewById } from '../../core/store/layout.selectors';
import { currentProperties } from '../../core/auth/auth.selectors';
import { isNil, isNumber } from 'lodash-es';
import { I18nService } from '../../core/i18n';

@Injectable()
export class VisitorsEffects {
    private i18n = inject(I18nService);

    loadAllVisitors$ = createEffect(() => this.actions$.pipe(
        ofType(VisitorsActions.AllVisitorsRequested),
        switchMap(action => combineLatest([
            of(action),
            this.store.pipe( select(currentProperties), filter(p => !isNil(p) ), take(1)),
            this.store.pipe( select(selectViewMode), take(1)),
            this.store.pipe( select(selectSelectedAgent), filter(a => !isNil(a) ), take(1)),
            this.store.pipe( select(selectSelectedProperty), take(1)),
        ])),
        switchMap( ([action, properties, view_mode, agent, property_id]) => {
            var p = (property_id != 0) ? property_id : properties.map(q => q.id).join(',');   
            return this.converseService.getVisitors( p, view_mode, agent.id, action.paging.page)
        }),
        map(vs => VisitorsActions.AllVisitorsRequestSuccess({visitors: vs}) )
    ));

    loadVisitor$ = createEffect(() => this.actions$.pipe(
        ofType(VisitorsActions.VisitorRequested),
        mergeMap( action => this.converseService.getVisitor( action.visitor_id )),
        map( (res) => 
            VisitorsActions.VisitorRequestSuccess({ visitor: res.model})
            //SessionsForVisitorLoaded({Sessions: res.model.sessions} )
        )
    ));

    visitorAttributeUpdate$ = createEffect(() => this.actions$.pipe(
        ofType(VisitorsActions.VisitorAttributeUpdate),
        switchMap( action => combineLatest([
            of(action),
            this.store.select(currentUser),
            this.store.select(selectViewMode),
            this.store.select(currentProperties),
            this.store.select(selectSelectedAgent),
            this.store.select(selectSelectedProperty),
        ]).pipe(take(1))),
        switchMap(([action, current_user, viewMode, properties, selected_agent, property_id]) =>
            this.store.select(selectVisitorExists(action.visitor_id)).pipe(
                take(1),
                map(visitorExists => ({
                    action,
                    viewMode,
                    selected_agent,
                    property_id,
                    properties,
                    visitorExists
                }))
            )
        ),     
        switchMap( ({action, viewMode, selected_agent, property_id, properties, visitorExists}) => {
            var p = (property_id != 0) ? property_id : properties.map(q => q.id).join(',');   

            // this is a custom view
            return this.converseService.visitorExistsinView(action.visitor_id, p, viewMode, selected_agent.id).pipe(
                switchMap(exists => exists
                    ? of(VisitorsActions.VisitorRequested({ visitor_id: exists.id }))
                    : EMPTY
                ),
                catchError((err: HttpErrorResponse) => {
                    if (err.status === 404) {
                        return EMPTY; // visitor not in view, do nothing
                    }
                    return EMPTY; // or dispatch an error action for other failures
                })
            );

        })
    ));

    /** 
     * When the visitor is selected we want to 
     * 1. load the extra info
     * 2. flush the unread count
     * 3. focus on the message box 
     */
    visitorSelected$ = createEffect(() => this.actions$.pipe(
        ofType(VisitorsActions.VisitorSelected), 
        mergeMap(action => this.converseService.getVisitor(action.visitor_id) ),
        withLatestFrom(this.store.select(selectViewMode)),
        switchMap( ([res,view_mode]) => {
            const visitor_update: Update<Visitor> = {
                id: res.model.id,
                changes: {
                    ...res.model,
                    unread_count: 0
                }
            };
            return [
                VisitorsActions.VisitorUpdateSuccess({ visitor: visitor_update}),
                LayoutActions.FocusMessageBox(),
                LayoutActions.persistVisitorSelection({ visitor_id: res.model.id, 
                    view: view_mode})
                
            ];
        })
    ));

    /**
     * Whenever the new visitor list is done loading, preselect the right visitor
     * depending on the current view mode
     */
    visitorsLoaded$ = createEffect(() => this.actions$.pipe(
        ofType(VisitorsActions.AllVisitorsRequestSuccess),
        withLatestFrom( this.store.select(selectVisitorPerCurrentView)),
        filter( ([action, visitor_id ]) => 
            // filter if no vid is selected in any view
            ( !isNil(visitor_id ) )  
        ),
        map( ([action, visitor_id]) => {
            return VisitorsActions.VisitorSelected({visitor_id: visitor_id})
        }) 
    ));

    /** Live Visitors */
    visitorInitiateConversation$ = createEffect(() => this.actions$.pipe(
        ofType(VisitorsActions.VisitorInitiateConversation),
        tap( () => this.router.navigateByUrl('/conversations')),
        switchMap(action => combineLatest([
            of(action),
            this.store.pipe(select(selectVisitorById(action.visitor_id)), take(1)),
            // wait for the visitor loaded event, otherwise the new visitor will get overwritten
            this.store.pipe(select(allVisitorsLoaded), filter(loaded => loaded))
        ])),
        switchMap( ([action, v, is_all_visitors_loaded]) => {

            if (v == null){
                /* @todo redundant since it will make two same http requests */
                return [
                    VisitorsActions.VisitorRequested({visitor_id: action.visitor_id }),
                    VisitorsActions.VisitorSelected({visitor_id: action.visitor_id})
                ];
            } else {
                return [VisitorsActions.VisitorSelected({visitor_id: action.visitor_id})];
            }

        }
        
        )
    ));

    saveVisitorInfo$ = createEffect(() => this.actions$.pipe(
        ofType(VisitorsActions.VisitorInfoSaveRequest),
        mergeMap( action => 
            this.converseService.saveVisitorInfo(action.visitor.id, action.visitor.changes).pipe(
                switchMap (res => {
                    const visitor_update: Update<Visitor> = {
                        id: res.model.id,
                        changes: {
                            ...res.model
                        }
                    };
                    return [
                        VisitorsActions.VisitorInfoSaveSuccess({visitor: visitor_update}),
                        ToastrActions.showToastrSuccess({message: this.i18n.t('inbox.visitors.visitor_details_were_updated', 'Visitor details were updated successfully.') , title: this.i18n.t('inbox.visitors.visitor_updated', 'Visitor Updated')})
                    ]
                }),
                catchError((er: HttpErrorResponse) => of(
                    ToastrActions.showToastrError({message: this.i18n.t('inbox.visitors.problems_while_updating_the_visitor_info', 'Problems while updating the visitor info. The error has been reported.') , title: this.i18n.t('common.something_went_wrong', 'Something went wrong')})
                ))
            )
        )
    ));

    visitorOffline$ = createEffect(() => this.actions$.pipe(
        ofType(VisitorsActions.VisitorOfflineReceived),
        switchMap( (action) => {

            const visitor_update: Update<Visitor> = {
                id: action.visitor_id,
                changes: {
                    offline_at: (new Date().toISOString())
                }
            };
            return [
                VisitorsActions.VisitorInfoSaveSuccess({visitor: visitor_update})
            ];
        })
    ));

    visitorOnline$ = createEffect(() => this.actions$.pipe(
        ofType(VisitorsActions.VisitorOnlineReceived),
        switchMap( (action) => {

            const visitor_update: Update<Visitor> = {
                id: action.visitor_id,
                changes: {
                    last_seen: (new Date().toISOString())
                }
            };
            return [
                VisitorsActions.VisitorInfoSaveSuccess({visitor: visitor_update})
            ];
        })
    ));

    detectVisitorLanguage$ = createEffect(() => this.actions$.pipe(
        ofType(VisitorsActions.DetectLanguageRequest),
        mergeMap( action => 
            this.converseService.detectLanguage(action.visitor_id).pipe(
                switchMap(res => of(
                    VisitorsActions.DetectLanguageSuccess({language: res.model.language})
                )),
                catchError((er: HttpErrorResponse) => of(
                    ToastrActions.showToastrError({message: this.i18n.t('inbox.visitors.couldn_t_detect_language', 'Couldn\'t detect language.') , title: this.i18n.t('common.something_went_wrong', 'Something went wrong')})
                ))
            )
        )
    ));

    liveTranslationRequest$ = createEffect(() => this.actions$.pipe(
        ofType(VisitorsActions.LiveTranslationRequest),
        mergeMap( action => 
            this.converseService.saveVisitorInfo(action.visitor.id, action.visitor.changes).pipe(
                switchMap (res => {
                    const visitor_update: Update<Visitor> = {
                        id: res.model.id,
                        changes: {
                            ...res.model
                        }
                    };
                    return [
                        VisitorsActions.VisitorInfoSaveSuccess({visitor: visitor_update}),
                        VisitorsActions.TranslateLastSessionRequest({visitor_id: res.model.id}),
                        ToastrActions.showToastrSuccess({message: this.i18n.t('inbox.visitors.live_translation_in_now_enabled', 'Live Translation in now Enabled.') , title: ''})
                    ]
                }),
                catchError((er: HttpErrorResponse) => of(
                    ToastrActions.showToastrError({message: this.i18n.t('inbox.visitors.problems_while_enabling_live', 'Problems while enabling Live Translation. The error has been reported.') , title: this.i18n.t('common.something_went_wrong', 'Something went wrong')})
                ))
            )
        )
    ));

    translateLastSessionRequest$ = createEffect(() => this.actions$.pipe(
        ofType(VisitorsActions.TranslateLastSessionRequest),
        mergeMap( action => 
            this.converseService.translateLastSession(action.visitor_id).pipe(
                switchMap (res => [
                        ActivityActions.ActivitiesOfVisitorRequested({visitor_id: action.visitor_id}),
                    ]
                ),
                catchError((er: HttpErrorResponse) => of(
                    ToastrActions.showToastrError({message: this.i18n.t('inbox.visitors.problems_while_translating_previous', 'Problems while translating previous messages. The error has been reported.') , title: this.i18n.t('common.something_went_wrong', 'Something went wrong')})
                ))
            )
        )
    ));

    reassignSessionRequest$ = createEffect(() => this.actions$.pipe(
        ofType(VisitorsActions.ReassignSessionRequest),
        mergeMap( action => 
            this.converseService.reassignSession(action.visitor_id, action.agent_id).pipe(
                switchMap (res => {

                        const visitor_update: Update<Visitor> = {
                            id: res.model.id,
                            changes: {
                                ...res.model
                            }
                        };

                        return[
                            VisitorsActions.VisitorInfoSaveSuccess({visitor: visitor_update}),
                            ToastrActions.showToastrSuccess({message: this.i18n.t('inbox.visitors.assignee_updated_successfully_and_was', 'Assignee updated successfully and was notified.') , title: ''})
                        ];

                }
                ),
                catchError((er: HttpErrorResponse) => of(
                    ToastrActions.showToastrError({message: this.i18n.t('inbox.visitors.problems_while_reassigning_session_the', 'Problems while reassigning session. The error has been reported.') , title: this.i18n.t('common.something_went_wrong', 'Something went wrong')})
                ))
            )
        )
    ));

    ReassignSessionUpdateReceived$ = createEffect(() => this.actions$.pipe(
        ofType(VisitorsActions.ReassignSessionUpdateReceived),
        switchMap( action => combineLatest([
            of(action),
            this.store.select(currentUser),
            this.store.select(selectInboxView), 
            this.store.select(selectViewMode),
            this.store.select(selectSelectedAgentId),
            this.store.select(currentProperties),
            this.store.select(selectSelectedProperty)
        ]).pipe(take(1))),
        mergeMap( ([action, currentUser, inboxMode, viewMode, selectedAgentId, properties, selected_property_id]) =>  {

            if (inboxMode == 'messages' && isNumber(viewMode) ) {
                // we have a custom view open, check the assignee in the current custom view
                var p = (selected_property_id != 0) ? selected_property_id : properties.map(q => q.id).join(',');   

                return this.converseService.visitorExistsinView(action.visitor_id, p, viewMode, selectedAgentId).pipe(
                    switchMap(visitorExists => of (
                        VisitorsActions.VisitorRequested({visitor_id: action.visitor_id}),
                        ToastrActions.showToastrInfo({message: this.i18n.t('inbox.visitors.assigned_you_a_new_session', '{{name}} has assigned you a new session.', {name: action.assigner_name}) , title: ''})

                    )),
                    catchError((err: HttpErrorResponse) => {
                        if (err.status === 404) 
                            return (currentUser?.id == action.assignee_id) ? of(
                                ToastrActions.showToastrInfo({message: this.i18n.t('inbox.visitors.assigned_you_a_new_session', '{{name}} has assigned you a new session.', {name: action.assigner_name}) , title: ''})
                            ) : EMPTY;
                        return EMPTY;
                    })
                );
            } else if (inboxMode == 'messages' && ( (viewMode == "agent" && selectedAgentId == action.assignee_id) || (viewMode == "open") )){
                // if im on the messages view and agent mode and the selected agent is me
                return of(
                    VisitorsActions.VisitorRequested({visitor_id: action.visitor_id}),
                    ToastrActions.showToastrInfo({message: this.i18n.t('inbox.visitors.assigned_you_a_new_session', '{{name}} has assigned you a new session.', {name: action.assigner_name}) , title: ''})
                );   

            } else if (currentUser?.id == action.assignee_id){
                // if the reassignment is for me but im not on the messages view, just show a toastr notification
                return of(
                    ToastrActions.showToastrInfo({message: this.i18n.t('inbox.visitors.assigned_you_a_new_session', '{{name}} has assigned you a new session.', {name: action.assigner_name}) , title: ''})
                );
            }
            return EMPTY;
        }
              
    )));
    
    /*setSelectedAgent = createEffect(() => this.actions$.pipe(
        ofType(VisitorsActions.SetSelectedAgent),
        map( () => VisitorsActions.AllVisitorsRequested( {paging: { page: 0 } } ))  
    ));*/

    searchVisitors$ = createEffect(() => this.actions$.pipe(
        ofType(VisitorsActions.SearchVisitorsRequested),
        switchMap(action => combineLatest([
            of(action),
            this.store.pipe(select(currentProperties), filter(p => !isNil(p)), take(1)),
            this.store.pipe(select(selectSelectedProperty), take(1)),
        ])),
        switchMap(([action, properties, property_id]) => {
            var p = (property_id != 0) ? property_id : properties.map(q => q.id).join(',');
            return this.converseService.searchVisitors(p, action.search, action.paging.page).pipe(
                map(vs => VisitorsActions.SearchVisitorsSuccess({ visitors: vs })),
                catchError((er: HttpErrorResponse) => of(
                    ToastrActions.showToastrError({ message: this.i18n.t('inbox.visitors.error_searching_visitors', 'Error searching visitors.'), title: this.i18n.t('inbox.visitors.search_failed', 'Search Failed') })
                ))
            );
        })
    ));
       
    constructor(
        private converseService: ConversationsService,
        private actions$: Actions,
        private store: Store<AppState>,
        private router: Router
    ) { }
}
